/**
 * Timestamp handling — the round-trip contract (§0.3).
 *
 * Internal representation: epoch WHOLE SECONDS (integer) or null.
 * Writer: ISO-8601 truncated to whole seconds, UTC "Z" (no fractional seconds,
 *         never the {seconds,nanoseconds} object form).
 * Reader: accept the ISO string (floor to whole seconds); also tolerate the
 *         legacy {seconds,nanoseconds} object and a bare epoch number. Never throw.
 */
import type { Epoch } from "../model.ts";

/** Parse any wire form of a timestamp into epoch whole seconds, or null. */
export function parseTimestamp(value: unknown): Epoch {
  if (value == null) return null;

  // ISO-8601 string (canonical). Also tolerates fractional/offset forms.
  if (typeof value === "string") {
    const ms = Date.parse(value);
    if (Number.isNaN(ms)) return null;
    return Math.floor(ms / 1000);
  }

  // Bare epoch number: seconds if it looks like seconds, else ms.
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    // Heuristic: > 1e12 is almost certainly milliseconds.
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }

  // Legacy Firestore-style object { seconds, nanoseconds }.
  if (typeof value === "object") {
    const s = (value as Record<string, unknown>)["seconds"];
    if (typeof s === "number" && Number.isFinite(s)) return Math.floor(s);
  }

  return null;
}

/** Format epoch whole seconds as ISO-8601 UTC with no fractional part, or null. */
export function formatTimestamp(epoch: Epoch): string | null {
  if (epoch == null || !Number.isFinite(epoch)) return null;
  // Truncate to whole seconds, then drop the ".000" millisecond part.
  return new Date(Math.floor(epoch) * 1000).toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function nowEpochSeconds(): number {
  return Math.floor(Date.now() / 1000);
}
